// API Response Types
export interface ApiResponse<T = any> {
  status: 'success' | 'error';
  data?: T;
  error?: string;
  errorCode?: string;
}

// Captcha Types
export type CaptchaType = 
  | 'text'
  | 'image' 
  | 'recaptcha_v2'
  | 'recaptcha_v2_invisible'
  | 'recaptcha_v3'
  | 'hcaptcha'
  | 'funcaptcha'
  | 'geetest'
  | 'geetest_v4'
  | 'turnstile';

export type TaskStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Base Captcha Data
export interface BaseCaptchaData {
  proxy?: string;
  proxytype?: string;
  userAgent?: string;
  cookies?: string;
}

// Text Captcha
export interface TextCaptchaData extends BaseCaptchaData {
  text: string;
  instructions?: string;
  minLength?: number;
  maxLength?: number;
  numeric?: boolean;
  caseSensitive?: boolean;
  language?: string;
}

// Image Captcha
export interface ImageCaptchaData extends BaseCaptchaData {
  image?: string; // base64 encoded
  imageUrl?: string;
  instructions?: string;
  minLength?: number;
  maxLength?: number;
  numeric?: boolean;
  caseSensitive?: boolean;
  language?: string;
}

// reCAPTCHA v2/v3
export interface RecaptchaData extends BaseCaptchaData {
  sitekey: string;
  pageurl: string;
  action?: string; // for v3
  minScore?: number; // for v3
  enterprise?: boolean;
  invisible?: boolean; // for v2
}

// hCaptcha
export interface HCaptchaData extends BaseCaptchaData {
  sitekey: string;
  pageurl: string;
  invisible?: boolean;
}

// Turnstile
export interface TurnstileData extends BaseCaptchaData {
  sitekey: string;
  pageurl: string;
  action?: string;
  cdata?: string;
}

// FunCaptcha
export interface FunCaptchaData extends BaseCaptchaData {
  publickey: string;
  pageurl: string;
  surl?: string;
  data?: Record<string, any>;
}

// GeeTest
export interface GeeTestData extends BaseCaptchaData {
  gt: string;
  challenge: string;
  pageurl: string;
  api_server?: string;
}

// GeeTest v4
export interface GeeTestV4Data extends BaseCaptchaData {
  captcha_id: string;
  pageurl: string;
}

// Submit Request Types
export type SubmitCaptchaRequest = 
  | ({ type: 'text' } & TextCaptchaData)
  | ({ type: 'image' } & ImageCaptchaData)
  | ({ type: 'recaptcha_v2' | 'recaptcha_v2_invisible' | 'recaptcha_v3' } & RecaptchaData)
  | ({ type: 'hcaptcha' } & HCaptchaData)
  | ({ type: 'turnstile' } & TurnstileData)
  | ({ type: 'funcaptcha' } & FunCaptchaData)
  | ({ type: 'geetest' } & GeeTestData)
  | ({ type: 'geetest_v4' } & GeeTestV4Data);

// Task Response
export interface TaskResponse {
  taskId: string;
  cost: number;
}

// Task Result
export interface TaskResult {
  status: TaskStatus;
  solution?: string;
  cost: number;
  processingTime?: number;
  error?: string;
}

// User Balance
export interface UserBalance {
  balance: number;
  currency: string;
}

// User Task
export interface UserTask {
  id: string;
  type: CaptchaType;
  status: TaskStatus;
  cost: number;
  createdAt: string;
  completedAt?: string;
  solution?: string;
  error?: string;
}

// Pagination
export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Client Configuration
export interface ClientConfig {
  apiKey: string;
  baseURL?: string;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

// Error Types
export class CaptchaSolverError extends Error {
  public readonly code?: string;
  public readonly statusCode?: number;

  constructor(message: string, code?: string, statusCode?: number) {
    super(message);
    this.name = 'CaptchaSolverError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

export class ValidationError extends CaptchaSolverError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR', 400);
    this.name = 'ValidationError';
  }
}

export class AuthenticationError extends CaptchaSolverError {
  constructor(message: string = 'Invalid API key') {
    super(message, 'AUTHENTICATION_ERROR', 401);
    this.name = 'AuthenticationError';
  }
}

export class InsufficientBalanceError extends CaptchaSolverError {
  constructor(message: string = 'Insufficient balance') {
    super(message, 'INSUFFICIENT_BALANCE', 402);
    this.name = 'InsufficientBalanceError';
  }
}

export class RateLimitError extends CaptchaSolverError {
  constructor(message: string = 'Rate limit exceeded') {
    super(message, 'RATE_LIMIT_EXCEEDED', 429);
    this.name = 'RateLimitError';
  }
}

export class TimeoutError extends CaptchaSolverError {
  constructor(message: string = 'Request timeout') {
    super(message, 'TIMEOUT_ERROR', 408);
    this.name = 'TimeoutError';
  }
}
